// Import necessary dependencies and type definitions from the local types file
import { create } from 'zustand';
import { HighlightedMappingMapType, MappingsType, RowIdToDescriptorIdConfidenceMapType } from '../types';
import { useShallow } from 'zustand/react/shallow';

type MappingsStateType = {
    // Currently active row ID
    draggedItemId: string | undefined;

    // ID of the mapping currently being hovered over
    draggedOverItemId: string | undefined;

    // Map of confidences for row IDs and descriptor IDs
    confidencesMap: RowIdToDescriptorIdConfidenceMapType;

    // Map of all mappings
    mappings: MappingsType;

    // Map of highlighted mappings
    highlightedMappingsMap: HighlightedMappingMapType;
};

/** Define the structure and operations for the metadata store */
type MappingStoreType = MappingsStateType & {
    // Method to update/set the active id
    setDraggedItemId: (id?: string) => void;

    // Method to update/set the over id
    setDraggedOverItemId: (id?: string) => void;

    // Method for complete replacement of confidencesMap
    setConfidencesMap: (confidencesMap: RowIdToDescriptorIdConfidenceMapType) => void;

    // Method for complete replacement of mappingsMap
    setMappings: (mappings: MappingsType) => void;

    // Method for complete replacement of highlightedMappingsMap
    setHighlightedMappingsMap: (highlightedMappingsMap: HighlightedMappingMapType) => void;

    // Method for complete replacement of store state
    setStoreState: (state: MappingsStateType) => void;
};

// Create a Zustand store for managing mapping-related state
const useMappingStore = create<MappingStoreType>()((set) => ({
    // Initial state with empty/undefined values
    draggedItemId: undefined,
    draggedOverItemId: undefined,
    confidencesMap: {},
    mappings: [],
    highlightedMappingsMap: {},

    /** Sets/Updates the currently active id */
    setDraggedItemId: (id) => set(() => ({ draggedItemId: id })),

    /** Sets/Updates the current over id */
    setDraggedOverItemId: (id) => set(() => ({ draggedOverItemId: id })),

    /** Completely replace confidencesMap */
    setConfidencesMap: (confidencesMap) => set(() => ({ confidencesMap })),

    /** Completely replace mappingsMap */
    setMappings: (mappings) => set(() => ({ mappings: mappings })),

    /** Completely replace highlightedMappingsMap */
    setHighlightedMappingsMap: (highlightedMappingsMap) => set(() => ({ highlightedMappingsMap })),

    /** Completely replace entire state */
    setStoreState: (state) =>
        set(() => ({
            draggedItemId: state.draggedItemId,
            draggedOverItemId: state.draggedOverItemId,
            confidencesMap: state.confidencesMap,
            mappings: state.mappings,
            highlightedMappingsMap: state.highlightedMappingsMap,
        })),
}));

export const useMappings = () => useMappingStore(useShallow((state) => state.mappings));

export const useSetMappingStoreState = () => useMappingStore(useShallow((state) => state.setStoreState));

export const useHighlightedMappingsMap = () => useMappingStore(useShallow((state) => state.highlightedMappingsMap));

export const useSetHighlightedMappingsMap = () =>
    useMappingStore(useShallow((state) => state.setHighlightedMappingsMap));

export const useIsActive = <T>(id: T) => useMappingStore(useShallow((state) => state.draggedItemId === id));

export const useIsOver = <T>(id: T) => useMappingStore(useShallow((state) => state.draggedOverItemId === id));

export const useDragAndDropState = () => useMappingStore(useShallow((state) => ({ activeId: state.draggedItemId })));

export const useDragAndDropStateSetters = () =>
    useMappingStore(
        useShallow((state) => ({ setOverId: state.setDraggedOverItemId, setActiveId: state.setDraggedItemId }))
    );

export const useConfidencesMap = () => useMappingStore(useShallow((state) => state.confidencesMap));

// Export the store hook for use in other components
export default useMappingStore;
